import React, { useMemo, useEffect } from 'react';
import { DataTable } from '../../../../components';
import {
	getSelectedSubjects,
	getSelectedYears,
	getUrlQueryParams,
	getSelectedLocations,
	getSubjectTitle,
	getSubjectLevel,
	getAppSelectedLanguageId,
	getCountriesInGroup,
} from '../../../../actions';
import { setDefaultUrlQueryParamsForSubview, setUrlQueryParam } from '../../../../actions';
import { useAppContext } from '../../../../context/AppState.jsx';
import { defaultVariables } from '../../../../referenceData';
import { default as TableLegend } from '../TableLegend/index.jsx';
import i18n from '../../../../i18n';
import TitleWithNames from '../../../../components/NameTitle/index.jsx';

const DEFAULT_SIGNIFICANT_CHANGE = '95';

const normalizeSelectedYears = (years, fallbackYears) => {
	const filteredYears = years.filter((year) => Number.isFinite(year));
	if (filteredYears.length > 0) {
		return filteredYears;
	}
	if (Array.isArray(fallbackYears) && fallbackYears.length > 0) {
		return fallbackYears.slice();
	}
	return [];
};

const getSubjectCategoryParent = (state, parentId) => {
	const item = state.fullListOfMeasures.filter((measure) => measure.s_id === parentId)[0];

	if (!parentId) {
		return false;
	}

	if (!item) {
		return false;
	}

	if (item.lvl == 1) {
		return item;
	}

	const parent = state.fullListOfMeasures.find((el) => el.s_id == item.ps_id);
	if (!parent || parent.s_id == parentId) {
		// should never happen, but best not to crash
		return state.fullListOfMeasures.find((e) => e.s_id == 'GOVERNANCE');
	}
	return getSubjectCategoryParent(state, parent.s_id);
};

const shortenedYear = (year) => {
	if (year === undefined || year === null) {
		return '';
	}
	return `\'${String(year).substring(2)}`;
};

const createTableHeader = (selectedLocations, selectedYearsRange1) => {
	const tableHeader = { firstColumn: {}, records: [], annualFields: {} };

	if (selectedLocations.length > 1) {
		tableHeader.firstColumn = 'Location';
	} else if (selectedLocations.length === 1) {
		tableHeader.firstColumn = 'Measure';
	} else {
		tableHeader.firstColumn = 'Measure';
	}

	if (selectedYearsRange1.length > 0) {
		const range1from = selectedYearsRange1[0];
		const range1to = selectedYearsRange1[selectedYearsRange1.length - 1];
		tableHeader.records.push(range1from);
		tableHeader.records.push(range1to);

		tableHeader.annualFields.AAT_Range_1_Rounded = [];
		tableHeader.annualFields.AAT_Range_1_Rounded.push(
			i18n.t('Change'),
			`${shortenedYear(range1from)}-${shortenedYear(range1to)}`,
		);
	} else {
		tableHeader.annualFields.AAT_Range_1_Rounded = [i18n.t('Change'), ''];
	}

	return tableHeader;
};

const supplementSelectedItemsBytFullContext = (state, selectedLocations, selectedSubjects) => {
	if (selectedSubjects.length === 1) {
		const listOfRemainingCountries = state.fullListOfCountries.filter(
			(country) => !selectedLocations.includes(country.iso),
		);

		return {
			listOfAllLocations: [...listOfRemainingCountries.map((country) => country.iso), ...selectedLocations],
			listOfAllSubjects: selectedSubjects,
		};
	} else {
		let subjectsSuplementedByFullContext = [];

		selectedSubjects.forEach((subject) => {
			const subjectLevel = getSubjectLevel(state, subject);
			if (subjectLevel === 1 && !subjectsSuplementedByFullContext.includes('GOVERNANCE')) {
				subjectsSuplementedByFullContext.push('GOVERNANCE');
			}
			if (subjectLevel === 1 && !subjectsSuplementedByFullContext.includes(subject)) {
				subjectsSuplementedByFullContext.push(subject);
			}
			if (subjectLevel !== 1 && subjectLevel !== 0) {
				const categoryParent = getSubjectCategoryParent(state, subject);
				if (!subjectsSuplementedByFullContext.includes(categoryParent.s_id)) {
					subjectsSuplementedByFullContext.push(categoryParent.s_id);
				}
			}
		});

		return {
			listOfAllLocations: selectedLocations,
			listOfAllSubjects: subjectsSuplementedByFullContext,
		};
	}
};

export default function SignificantChangesTable({ urlParams }) {
	const [state, dispatch] = useAppContext();

	if (
		typeof urlParams.range1from === 'undefined' ||
		typeof urlParams.range1to === 'undefined' ||
		typeof urlParams.range2from === 'undefined' ||
		typeof urlParams.range2to === 'undefined'
	) {
		setDefaultUrlQueryParamsForSubview(urlParams);
	}

	const selectedYearsRange1 = normalizeSelectedYears(
		getSelectedYears('range1from', 'range1to'),
		defaultVariables.default_year_array,
	);
	const selectedYearsRange2 = normalizeSelectedYears(
		getSelectedYears('range2from', 'range2to'),
		defaultVariables.default_secondary_year_array,
	);
	const selectedSubjects = getSelectedSubjects(state);
	urlParams = getUrlQueryParams();
	const selectedLocations = getSelectedLocations(state);

	useEffect(() => {
		if (!urlParams?.significantChange) {
			dispatch({
				type: 'setUrlQueryParam',
				action: setUrlQueryParam({ name: 'significantChange', value: DEFAULT_SIGNIFICANT_CHANGE }),
			});
		}
	}, [dispatch, urlParams?.significantChange]);

	const measuresView = useMemo(() => selectedLocations.length === 1, [selectedLocations]);
	const tableTitle = useMemo(
		() => (
			<TitleWithNames
				subject={getSubjectTitle(state)}
				locations={selectedLocations}
				suffix={i18n.t(' - Significant Changes')}
			/>
		),
		[selectedLocations, state],
	);
	const parseUrlParam = (value, defaultValue = null) => {
		if (value === undefined || value === 'undefined' || value === null) {
			return defaultValue;
		}
		try {
			return JSON.parse(value);
		} catch (e) {
			return defaultValue;
		}
	};

	const filters = {
		showAAT: parseUrlParam(urlParams?.showAAT, null),
		showHighest: false,
		showLowest: false,
		showHighlights: false, // Click-to-highlight intentionally disabled
		showSignificantChanges: true,
		significantChange: parseUrlParam(urlParams?.significantChange, DEFAULT_SIGNIFICANT_CHANGE),
		groupHighlight: urlParams?.groupHighlight ?? '',
		sortBy: urlParams?.sortBy?.toString() || null,
		sortDir: urlParams?.sortDir || null,
		subview: urlParams?.subview || 'score',
		visibleRecordColumns:
			selectedYearsRange1.length > 0
				? [selectedYearsRange1[0].toString(), selectedYearsRange1[selectedYearsRange1.length - 1].toString()]
				: [],
		collapseAll: parseUrlParam(urlParams?.collapseAll, false),
		showFullContext: parseUrlParam(urlParams?.showFullContext, false),
		...parseUrlParam(urlParams?.filters, {}),
	};

	const hasDefaultYearsSelected =
		selectedYearsRange1.length > 0 &&
		selectedYearsRange2.length > 0 &&
		selectedYearsRange1[0] === defaultVariables.default_year_array[0] &&
		selectedYearsRange1[selectedYearsRange1.length - 1] ===
			defaultVariables.default_year_array[defaultVariables.default_year_array.length - 1] &&
		selectedYearsRange2[0] === defaultVariables.default_secondary_year_array[0] &&
		selectedYearsRange2[selectedYearsRange2.length - 1] ===
			defaultVariables.default_secondary_year_array[defaultVariables.default_secondary_year_array.length - 1];

	useEffect(() => {
		const worker = new Worker('./dataTableWebWorker.js', { type: 'module' });

		let selectedItemsSuplementedByFullContext;

		if (JSON.parse(urlParams.showFullContext || false)) {
			selectedItemsSuplementedByFullContext = supplementSelectedItemsBytFullContext(
				state,
				selectedLocations,
				selectedSubjects,
			);
		} else {
			selectedItemsSuplementedByFullContext = {
				listOfAllLocations: selectedLocations,
				listOfAllSubjects: selectedSubjects,
			};
		}

		worker.postMessage({
			selectedLocations: selectedItemsSuplementedByFullContext.listOfAllLocations,
			selectedSubjects: selectedItemsSuplementedByFullContext.listOfAllSubjects,
			selectedYears:
				selectedYearsRange1.length > 0
					? [selectedYearsRange1[0], selectedYearsRange1[selectedYearsRange1.length - 1]]
					: [],
			selectedYears2: [],
			contextSubjects: selectedSubjects,
			urlParams,
			state,
			hasDefaultYearsSelected,
			defaultVariables,
			type: 'createSignificantChangesTable',
			activeLanguage: i18n.language,
		});

		worker.onerror = (err) => err;
		worker.onmessage = (e) => {
			if (e.data === 'loading') {
				dispatch({
					type: 'dataTable',
					payload: [],
				});
			} else {
				dispatch({
					type: 'dataTable',
					payload: e.data,
				});
				worker.terminate();
			}
		};
	}, [state.setUrlQueryParam, i18n.language]);

	const tableHeader = createTableHeader(selectedLocations, selectedYearsRange1);

	const tableRows = state?.dataTable || [];

	const measureTreeOrder = useMemo(() => state.measureTreeOrder, [state.measureTreeOrder]);

	const onInfoIconClick = (subjectName) => {
		const descriptor = state.descriptors.find(
			(subject) => subject.uid === `md${subjectName}` && subject.lang === getAppSelectedLanguageId(),
		);
		return descriptor.wd;
	};

	// this just puts it into appContext so that share modals etc can find these to
	// build complete URLs
	const onFilterChange = (_filterState) => {
		let filterState = {
			sortBy: _filterState.columnId,
			sortDir: _filterState.direction,
		};

		dispatch({
			type: 'tableFilters',
			payload: filterState,
		});
	};

	const countriesToHighlight = filters.groupHighlight
		? getCountriesInGroup(state, filters.groupHighlight).map((country) => country.iso)
		: [];

	return (
		<>
			<DataTable
				measuresView={measuresView}
				tableRows={tableRows}
				tableHeader={tableHeader}
				tableTitle={tableTitle}
				filters={filters}
				onInfoIconClick={onInfoIconClick}
				measureTreeOrder={measureTreeOrder}
				onFilterChange={onFilterChange}
				countriesToHighlight={countriesToHighlight}
			/>
			<TableLegend />
		</>
	);
}
